import { Component, inject, signal } from '@angular/core';
import { IonBackButton, IonButtons, IonContent, IonHeader, IonMenuButton, IonTitle, IonToolbar } from '@ionic/angular/standalone';
import { TranslatePipe } from '@ngx-translate/core';
import { Camera, CameraResultType, CameraSource, Photo } from '@capacitor/camera';
import { lastValueFrom } from 'rxjs';
import {
  WorkDocumentsV2Service,
  WorkDocumenttypeIdentifierNameEnum,
  WorkSendDocumentsByEmailV2Dto,
} from '@work-frontend-monorepo/work-rest-api';
import { ButtonComponent } from '@work-frontend-monorepo/work-ui/layout';
import { AlertDialogService } from '@work-frontend-monorepo/work-ui/modals';
import { ExceptionHandlingService } from '@work-frontend-monorepo/work-ui/services';
import { InputFieldComponent } from '@work-frontend-monorepo/work-ui/forms';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { LoadingAndFeedbackState } from '@work-frontend-monorepo/work-ui/utilities';
import { Store } from '@ngxs/store';
import { toSignal } from '@angular/core/rxjs-interop';
import { ProfileState } from '@work-frontend-monorepo/work-ui/state-management';

interface CapturedPhoto {
  dataUrl: string;
  blob: Blob;
  fileName: string;
}

@Component({
  selector: 'sfg-app-scan-und-upload-page',
  templateUrl: './scan-und-upload-page.component.html',
  styleUrls: ['./scan-und-upload-page.component.scss'],
  standalone: true,
  imports: [
    ButtonComponent,
    IonBackButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonMenuButton,
    IonTitle,
    IonToolbar,
    InputFieldComponent,
    ReactiveFormsModule,
    TranslatePipe,
  ],
})
export class ScanUndUploadPageComponent {
  capturedPhotos = signal<CapturedPhoto[]>([]);
  uploadLoadingState = signal<LoadingAndFeedbackState>('none');
  noteControl = new FormControl<string>('');
  readonly noteSuggestions = ['Spesen', 'Ausweis', 'Sonstiges'];

  protected applyNoteSuggestion(suggestion: string) {
    this.noteControl.setValue(suggestion);
  }

  private readonly store = inject(Store);
  private readonly profile = toSignal(this.store.select(ProfileState.profile));
  private readonly workDocumentsV2Service = inject(WorkDocumentsV2Service);
  private readonly alertDialogService = inject(AlertDialogService);
  private readonly exceptionHandlingService = inject(ExceptionHandlingService);

  protected async takePhoto() {
    await Camera.requestPermissions({ permissions: ['camera'] });
    await this.pickPhoto(CameraSource.Camera);
  }

  protected async pickFromGallery() {
    await Camera.requestPermissions({ permissions: ['photos'] });
    await this.pickPhoto(CameraSource.Photos);
  }

  protected removePhoto(index: number) {
    this.capturedPhotos.update((photos) => photos.filter((_, i) => i !== index));
  }

  protected async uploadAndSend() {
    if (this.capturedPhotos().length === 0 || this.uploadLoadingState() === 'loading') return;

    this.uploadLoadingState.set('loading');
    try {
      const uploadedDocumentIds: number[] = [];

      for (const photo of this.capturedPhotos()) {
        const uploaded = await lastValueFrom(
          this.workDocumentsV2Service.documentsV2ControllerUploadFile({
            body: {
              file: new File([photo.blob], photo.fileName, { type: 'image/jpeg' }),
              prettyName: photo.fileName,
              documentTypeIdentifierName: WorkDocumenttypeIdentifierNameEnum.AllgemeinesDokumentOhneWorkflow,
            },
          }),
        );
        uploadedDocumentIds.push(uploaded.id);
      }

      const note = this.noteControl.value ?? '';
      const profile = this.profile();
      const userName = profile ? `${profile.firstname} ${profile.lastname}` : '';
      const sendDto: WorkSendDocumentsByEmailV2Dto = {
        documentIds: uploadedDocumentIds,
        to: ['daniel.maegerli@digitalemenschen.ch'],
        subject: note ? `SFG-App Scan von ${userName}: ${note}` : `SFG-App Scan von ${userName}`,
        deleteDocumentsAfterSuccessfulSending: true,
      };

      await lastValueFrom(
        this.workDocumentsV2Service.documentsV2ControllerSendDocumentsByEmail({
          body: sendDto,
        }),
      );

      this.uploadLoadingState.set('success');
      this.alertDialogService.showAlert({
        title: 'SCAN_UPLOAD_ERFOLGREICH',
        feedbackState: 'success',
      });

      this.capturedPhotos.set([]);
      this.noteControl.reset('');
    } catch (uploadException) {
      this.uploadLoadingState.set('error');
      this.exceptionHandlingService.handleExceptionWithAlert('scanUndUploadException', uploadException);
    }
  }

  private async pickPhoto(source: CameraSource) {
    try {
      const photo: Photo = await Camera.getPhoto({
        quality: 80,
        allowEditing: false,
        resultType: CameraResultType.DataUrl,
        source,
        saveToGallery: false,
      });

      if (!photo.dataUrl) return;

      const blob = await this.dataUrlToBlob(photo.dataUrl);
      const fileName = `scan_${Date.now()}.jpg`;

      this.capturedPhotos.update((photos) => [...photos, { dataUrl: photo.dataUrl!, blob, fileName }]);
    } catch (error: unknown) {
      // User cancelled - kein Fehler anzeigen
      const message = error instanceof Error ? error.message : String(error);
      if (!message?.toLowerCase().includes('cancel') && !message?.toLowerCase().includes('dismissed')) {
        this.exceptionHandlingService.handleExceptionWithAlert('takePhotoException', error);
      }
    }
  }

  private async dataUrlToBlob(dataUrl: string): Promise<Blob> {
    const response = await fetch(dataUrl);
    return response.blob();
  }
}
